import { useState, useCallback, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Play } from 'lucide-react'
import type { Participante } from '../types/sorteos.types'

interface SlotMachineProps {
  participantes: Participante[]
  onWinner: (participante: Participante) => void
  disabled?: boolean
  maxGanadores?: number
  girosRestantes?: number
  children?: React.ReactNode
}

export function SlotMachine({ participantes, onWinner, disabled, girosRestantes, children }: SlotMachineProps) {
  const [isSpinning, setIsSpinning] = useState(false)
  const [winner, setWinner] = useState<Participante | null>(null)
  // Índice central actual (el que se muestra resaltado)
  const [currentIndex, setCurrentIndex] = useState(-1)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Obtener 5 nombres visibles: 2 antes, centro, 2 después
  const getVisibleNames = (centerIdx: number): string[] => {
    if (participantes.length === 0) return ['', '', 'Presiona para girar', '', '']
    const names: string[] = []
    for (let offset = -2; offset <= 2; offset++) {
      const idx = ((centerIdx + offset) % participantes.length + participantes.length) % participantes.length
      names.push(participantes[idx].nombre)
    }
    return names
  }

  const spin = useCallback(() => {
    if (participantes.length === 0 || isSpinning || disabled) return

    setIsSpinning(true)
    setWinner(null)

    let counter = 0
    const totalSpins = 30 + Math.floor(Math.random() * 20)
    const winnerIndex = Math.floor(Math.random() * participantes.length)

    intervalRef.current = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * participantes.length)
      setCurrentIndex(randomIndex)
      counter++

      if (counter >= totalSpins) {
        if (intervalRef.current) clearInterval(intervalRef.current)

        setCurrentIndex(winnerIndex)
        const selectedWinner = participantes[winnerIndex]
        setWinner(selectedWinner)
        setIsSpinning(false)
        onWinner(selectedWinner)
      }
    }, 50)
  }, [participantes, isSpinning, disabled, onWinner])

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const canSpin = participantes.length > 0 && !isSpinning && !disabled && (girosRestantes === undefined || girosRestantes > 0)
  const idle = currentIndex === -1 && !winner
  const visibleNames = idle ? ['', '', 'Presiona para girar', '', ''] : getVisibleNames(currentIndex)

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center gap-6">
          {/* Slot reel con 5 filas */}
          <div className="w-full max-w-md overflow-hidden rounded-xl border-2 border-gray-300 dark:border-gray-600">
            {visibleNames.map((name, i) => {
              const isCenter = i === 2
              return (
                <div
                  key={i}
                  className={`
                    flex items-center justify-center px-4 transition-all duration-100 truncate
                    ${isCenter
                      ? `h-14 text-lg font-bold border-y-2 ${
                          isSpinning
                            ? 'border-teal-500 bg-teal-500/10 text-teal-700 dark:text-teal-300'
                            : winner
                              ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-300'
                              : 'border-gray-200 dark:border-gray-700 text-foreground'
                        }`
                      : `h-9 text-sm text-muted-foreground ${
                          Math.abs(i - 2) === 2 ? 'opacity-30' : 'opacity-60'
                        }`
                    }
                  `}
                >
                  {name}
                </div>
              )
            })}
          </div>

          <Button
            size="lg"
            className="bg-teal-800 hover:bg-teal-900 text-white px-8"
            onClick={spin}
            disabled={!canSpin}
          >
            <Play className="h-5 w-5 mr-2" />
            {isSpinning ? 'Girando...' : 'Girar'}
          </Button>

          {girosRestantes !== undefined && (
            <p className="text-sm text-muted-foreground">
              Giros restantes: <span className="font-semibold text-black dark:text-white">{girosRestantes}</span>
            </p>
          )}

          {children && (
            <div className="w-full border-t border-gray-200 dark:border-gray-700 pt-4">
              {children}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
